<template>
  <div class="level-up-modal-overlay" v-if="showModal">
    <div class="level-up-modal">
      <h2 class="modal-title">Поздравляем!</h2>
      
      <p class="modal-text">Вы перешли на новый уровень</p>
      
      <div class="level-info">
        <h3 class="level-name">LVL {{ newLevel.level }} — {{ newLevel.name }}</h3>
        <div class="level-coin">
          <img :src="getCoinImage(newLevel.level)" alt="Level Coin" class="coin-image" />
        </div>
      </div>
      
      <div class="reward-section" v-if="reward > 0">
        <p class="reward-text">Награда: +{{ formatReward(reward) }}</p>
      </div>
      
      <button class="claim-button" @click="claimReward">
        Забрать
      </button>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';

// Импортируем все изображения заранее
import onePng from '../assets/one.png';
import twoPng from '../assets/two.png';
import threePng from '../assets/three.png';
import fourPng from '../assets/four.png';
import fivePng from '../assets/five.png';
import SixPng from '../assets/Six.png'; // Исправлен регистр
import sevenPng from '../assets/seven.png';
import eightPng from '../assets/eight.png';
import monetaPng from '../assets/Moneta.png';

export default {
  name: 'LevelUpModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    newLevel: {
      type: Object,
      required: true,
      validator: (value) => {
        return value && 
               typeof value.level === 'number' && 
               typeof value.name === 'string' &&
               value.level > 0 && 
               value.level <= 40;
      }
    },
    reward: {
      type: Number,
      default: 0,
      validator: (value) => {
        return typeof value === 'number' && value >= 0;
      }
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const showModal = ref(props.show);
    
    // Санитизация входных данных
    const sanitizeLevelName = (name) => {
      if (typeof name !== 'string') return 'Unknown Level';
      return name
        .trim()
        .replace(/[<>"'&]/g, '')
        .substring(0, 50);
    };
    
    const sanitizeLevelNumber = (level) => {
      const num = Number(level);
      return !isNaN(num) && num >= 1 && num <= 40 ? Math.floor(num) : 1;
    };
    
    const sanitizeReward = (reward) => {
      const num = Number(reward);
      return !isNaN(num) && num >= 0 ? Math.floor(num) : 0;
    };

    // Watch for prop changes
    const watchShow = () => {
      showModal.value = props.show;
    };

    // Маппинг изображений
    const coinImages = {
      1: onePng,
      2: twoPng,
      3: threePng,
      4: fourPng,
      5: fivePng,
      6: SixPng, // Исправлен регистр
      7: sevenPng,
      8: eightPng,
    };

    const getCoinImage = (level) => {
      // Load different coin images based on level
      try {
        // For levels 1-8, use specific images; for level 50 (or other high levels), use Moneta.png
        if (level >= 1 && level <= 8) {
          const levelKey = level.toString();
          if (coinImages[levelKey]) {
            return coinImages[levelKey];
          }
        }
        // For levels higher than 8, we continue using Moneta.png
        return monetaPng;
      } catch (error) {
        console.error('Error loading coin image:', error);
        return monetaPng;
      }
    };

    const formatReward = (amount) => {
      const cleanAmount = sanitizeReward(amount);
      // Format as whole number without decimals
      return Math.floor(cleanAmount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };

    const claimReward = () => {
      // Validate reward before emitting
      const cleanReward = sanitizeReward(props.reward);
      emit('close', cleanReward);
      showModal.value = false;
    };

    return {
      showModal,
      getCoinImage,
      formatReward,
      claimReward,
      sanitizeLevelName,
      sanitizeLevelNumber,
      sanitizeReward
    };
  }
};
</script>

<style scoped>
.level-up-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(8px);
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.level-up-modal {
  background: linear-gradient(145deg, #000000, #333333);
  border-radius: 25px;
  padding: 35px;
  width: 90%;
  max-width: 400px;
  text-align: center;
  border: 1px solid #808080;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.7),
              0 0 30px rgba(255, 255, 255, 0.3);
  transform: scale(1);
  animation: modalAppear 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

@keyframes modalAppear {
  0% { 
    transform: scale(0.8) translateY(50px);
    opacity: 0;
  }
  100% { 
    transform: scale(1) translateY(0);
    opacity: 1;
  }
}

.modal-title {
  font-family: 'Poppins', 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 15px 0;
  text-shadow: 0 0 15px rgba(255, 255, 255, 0.5);
  background: linear-gradient(to right, #ffffff, #cccccc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.modal-text {
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 18px;
  color: #ffffff;
  margin: 0 0 25px 0;
  font-weight: 500;
}

.level-info {
  margin: 25px 0;
}

.level-name {
  font-family: 'Poppins', 'Inter', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 20px 0;
  text-shadow: 0 0 10px rgba(255, 255, 255, 0.4);
}

.level-coin {
  margin: 20px 0;
  animation: coinPopIn 0.5s ease-out 0.2s both;
}

@keyframes coinPopIn {
  0% { 
    transform: scale(0) rotate(-10deg);
    opacity: 0;
  }
  70% {
    transform: scale(1.1) rotate(5deg);
  }
  100% { 
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.coin-image {
  width: 140px; /* Увеличенный размер */
  height: 140px; /* Увеличенный размер */
  object-fit: contain;
  filter: drop-shadow(0 0 20px rgba(255, 255, 255, 0.6)) 
          drop-shadow(0 0 30px rgba(255, 255, 255, 0.4));
  animation: floatGlow 3s ease-in-out infinite;
}

@keyframes floatGlow {
  0%, 100% { 
    transform: translateY(0px) scale(1);
    filter: drop-shadow(0 0 20px rgba(255, 255, 255, 0.6)) 
            drop-shadow(0 0 30px rgba(255, 255, 255, 0.4));
  }
  50% { 
    transform: translateY(-10px) scale(1.05);
    filter: drop-shadow(0 0 25px rgba(255, 255, 255, 0.8)) 
            drop-shadow(0 0 35px rgba(255, 255, 255, 0.6));
  }
}

.reward-section {
  margin: 25px 0;
  padding: 20px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.reward-text {
  font-family: 'Poppins', 'Inter', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  text-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
}

.claim-button {
  background: linear-gradient(135deg, #808080 0%, #a9a9a9 100%);
  color: #ffffff;
  border: none;
  border-radius: 15px;
  padding: 18px 35px;
  font-family: 'Inter', 'Roboto', sans-serif;
  font-size: 18px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  margin-top: 15px;
  box-shadow: 0 5px 15px rgba(128, 128, 128, 0.4);
  letter-spacing: 0.5px;
}

.claim-button:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(128, 128, 128, 0.6);
  background: linear-gradient(135deg, #a9a9a9 0%, #c0c0c0 100%);
}

.claim-button:active {
  transform: translateY(1px);
  box-shadow: 0 3px 10px rgba(128, 128, 128, 0.4);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .level-up-modal {
    padding: 25px;
    max-width: 350px;
  }
  
  .modal-title {
    font-size: 24px;
  }
  
  .level-name {
    font-size: 20px;
  }
  
  .coin-image {
    width: 120px; /* Меньший размер для мобильных */
    height: 120px;
  }
  
  .reward-text {
    font-size: 18px;
  }
  
  .claim-button {
    padding: 16px 30px;
    font-size: 16px;
  }
}
</style>